/**
 * API routing for the Electron desktop shell.
 *
 * The bundled desktop UI is served from a custom scheme (sanctuary-app://),
 * so relative `/api/*` calls can't reach the hosted Cloudflare Pages
 * functions directly. When the Electron preload API is available we route
 * those calls through the main process (no CORS, shares the session cookie
 * jar used by the system-browser OAuth handoff). Otherwise we fall back to
 * absolute URLs.
 */

const DESKTOP_PROTOCOLS = new Set(['file:', 'app:', 'sanctuary-app:']);

function isDesktopShellOrigin(): boolean {
  return typeof window !== 'undefined' && DESKTOP_PROTOCOLS.has(window.location.protocol);
}

export function getApiOrigin(): string {
  if (!isDesktopShellOrigin()) return '';
  const configured = (import.meta.env.VITE_APP_URL as string | undefined)?.trim();
  return (configured || 'https://sanctuaryslides.app').replace(/\/$/, '');
}

export function apiUrl(path: string): string {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  const origin = getApiOrigin();
  return origin ? `${origin}${normalized}` : normalized;
}

type DesktopApiFetch = (payload: {
  path: string;
  method?: string;
  headers?: Record<string, string>;
  body?: string;
}) => Promise<{
  ok: boolean;
  status: number;
  statusText: string;
  headers: Record<string, string>;
  body: string;
}>;

function getDesktopApiFetch(): DesktopApiFetch | undefined {
  if (typeof window === 'undefined') return undefined;
  return (window as any).sanctuary?.api?.fetch as DesktopApiFetch | undefined;
}

/** Extract an `/api/...` path when the request targets the app's API, else null. */
function extractApiPath(rawUrl: string): string | null {
  if (rawUrl.startsWith('/api/')) return rawUrl;

  try {
    const parsed = new URL(rawUrl, window.location.href);
    if (!parsed.pathname.startsWith('/api/')) return null;
    const origin = getApiOrigin();
    const isSameShellOrigin = parsed.origin === window.location.origin || parsed.origin === 'null';
    const isConfiguredApiOrigin = origin !== '' && parsed.origin === origin;
    if (isSameShellOrigin || isConfiguredApiOrigin) {
      return `${parsed.pathname}${parsed.search}`;
    }
  } catch {
    // not a parsable URL
  }
  return null;
}

function headersToRecord(headers: HeadersInit | undefined): Record<string, string> | undefined {
  if (!headers) return undefined;
  const record: Record<string, string> = {};
  new Headers(headers).forEach((value, key) => {
    record[key] = value;
  });
  return record;
}

async function fetchViaDesktopProxy(
  proxy: DesktopApiFetch,
  apiPath: string,
  init: RequestInit | undefined,
  request?: Request,
): Promise<Response> {
  let body: string | undefined;
  const rawBody = init?.body;
  if (typeof rawBody === 'string') {
    body = rawBody;
  } else if (rawBody != null) {
    // Non-string bodies (FormData, Blob, streams) aren't IPC-serializable.
    throw new Error('Unsupported request body for desktop API proxy');
  } else if (request && request.method !== 'GET' && request.method !== 'HEAD') {
    body = await request.clone().text();
  }

  const result = await proxy({
    path: apiPath,
    method: init?.method || request?.method || 'GET',
    headers: headersToRecord(init?.headers) ?? (request ? headersToRecord(request.headers) : undefined),
    body,
  });

  if (result.status === 0) {
    throw new TypeError(`Failed to fetch: ${result.statusText}`);
  }

  return new Response(result.body, {
    status: result.status,
    statusText: result.statusText,
    headers: result.headers,
  });
}

/** Patch fetch so relative `/api/*` calls work from the Electron desktop shell. */
export function installDesktopApiFetchPatch() {
  if (typeof window === 'undefined') return;
  if (!isDesktopShellOrigin()) return;
  if ((window as any).__sanctuaryApiFetchPatched) return;

  const origin = getApiOrigin();
  const originalFetch = window.fetch.bind(window);

  window.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
    const rawUrl = typeof input === 'string'
      ? input
      : input instanceof URL
        ? input.toString()
        : input.url;
    const apiPath = extractApiPath(rawUrl);

    if (apiPath) {
      const proxy = getDesktopApiFetch();
      if (proxy) {
        const request = input instanceof Request ? input : undefined;
        return fetchViaDesktopProxy(proxy, apiPath, init, request).catch((error) => {
          if (error instanceof Error && error.message.includes('Unsupported request body')) {
            // Fall back to a direct cross-origin fetch; may fail on CORS.
            return originalFetch(`${origin}${apiPath}`, { ...init, credentials: init?.credentials ?? 'include' });
          }
          throw error;
        });
      }
      return originalFetch(`${origin}${apiPath}`, {
        ...init,
        credentials: init?.credentials ?? 'include',
      });
    }

    return originalFetch(input as RequestInfo, init);
  }) as typeof window.fetch;

  (window as any).__sanctuaryApiFetchPatched = true;
}
